import { Injectable, inject, signal, computed } from '@angular/core';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { Capacitor } from '@capacitor/core';
import { Order, SourceType, type FormSettings } from '@crowdctrl/core';
import { ActionSheetController, AlertController } from '@ionic/angular/standalone';
import { ORDER_TICKET_PUBLIC_FORMS_ORIGIN } from '../tokens/order-ticket-public-forms-origin.token';
import { CrowdctrlToastService } from './crowdctrl-toast.service';
import { generateOrderTicketBarcodeDataUrl } from '../utils/order-ticket-barcode';
import QRCode from 'qrcode';
import html2canvas from 'html2canvas';

/** Data extracted from an order for the printed ticket */
export interface OrderTicketData {
  orderNumber: string;
  orderId: string;
  tenantName: string | null;
  workspaceName: string | null;
  sessionName: string | null;
  customerName: string | null;
  queueName: string | null;
  createdAt: string;
  showNumberLabel: boolean;
  numberLabelText: string;
  showQrCaption: boolean;
  qrCaptionText: string;
  showQueuePrefix: boolean;
  queuePrefixChars: string;
}

/** Brother printer selected for Android session (in-memory until disconnect). */
export interface BrotherAndroidSessionPrinter {
  /** `BRLMPrinterPort` string enum value (e.g. `"usb"`, `"wifi"`) — must not be coerced with `Number()`. */
  port: string;
  channelInfo: string;
  nodeName: string;
}

const USB_SEARCH_TIMEOUT_MS = 12000;
const USB_PRINTER_WAIT_MS = 7000;
const USB_RETRY_SEARCH_TIMEOUT_MS = 10000;
const USB_RETRY_PRINTER_WAIT_MS = 6000;
const WIFI_SEARCH_DURATION_SEC = 5;
const WIFI_PRINTER_WAIT_MS = WIFI_SEARCH_DURATION_SEC * 1000 + 500;
const RENDER_TIMEOUT_MS = 10000;
const PRINT_TIMEOUT_MS = 25000;
/** USB discovery during explicit Connect (aligns with native permission wait). */
const ANDROID_CONNECT_USB_SEARCH_MS = 36000;
const ANDROID_CONNECT_USB_WAIT_MS = 20000;
const ANDROID_CONNECT_USB_RETRY_SEARCH_MS = 36000;
const ANDROID_CONNECT_USB_RETRY_WAIT_MS = 16000;
/** html2canvas width; scaled from prior 62mm baseline for 54mm DK continuous non-adhesive roll. */
const TICKET_RENDER_WIDTH_PX = Math.round((348 * 54) / 62);
const TICKET_RENDER_SCALE = 2;
/** Inner width of the web print container (see printOrderTicketWeb). */
const WEB_TICKET_BARCODE_MAX_WIDTH_PX = 400 - 32;
const DEFAULT_PRINT_TICKET_NUMBER_LABEL = 'Your number';
const DEFAULT_PRINT_TICKET_QR_CAPTION = 'SCAN TO FOLLOW YOUR ORDER';
const DEFAULT_PRINT_TICKET_QUEUE_PREFIX = 'For ';
/** Queued-at line: short date (dd/mm/yy) + time + timezone abbreviation. */
const TICKET_DATE_FORMATTER = new Intl.DateTimeFormat('en-AU', {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
  timeZone: 'Australia/Sydney',
  timeZoneName: 'short',
});

type SessionFormPrintTicket = NonNullable<FormSettings['printTicket']>;

function formatTicketDate(date: Date | string | { toDate: () => Date } | undefined | null): string {
  if (!date) {
    return 'N/A';
  }

  const parsedDate =
    typeof date === 'string'
      ? new Date(date)
      : date instanceof Date
        ? date
        : typeof date.toDate === 'function'
          ? date.toDate()
          : null;

  if (!parsedDate || Number.isNaN(parsedDate.getTime())) {
    return 'N/A';
  }

  return TICKET_DATE_FORMATTER.format(parsedDate);
}

@Injectable({
  providedIn: 'root',
})
export class OrderTicketPrintService {
  private toastService = inject(CrowdctrlToastService);
  private actionSheetController = inject(ActionSheetController);
  private alertController = inject(AlertController);
  private firestore = inject(Firestore, { optional: true });
  private formsPublicOrigin = inject(ORDER_TICKET_PUBLIC_FORMS_ORIGIN, { optional: true });

  private readonly androidBrotherPrinterSignal = signal<BrotherAndroidSessionPrinter | null>(null);

  /** Android Brother printer session for kiosk/staff setup (null = not connected). */
  readonly androidBrotherPrinter = this.androidBrotherPrinterSignal.asReadonly();

  /** Whether the Print ticket control should show (web: yes; Android: only after Connect). */
  readonly showAndroidPrintTicketButton = computed(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
      return true;
    }
    return this.androidBrotherPrinterSignal() !== null;
  });

  isAndroidNativeBrotherPrint(): boolean {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
  }

  hasAndroidBrotherPrinter(): boolean {
    return this.androidBrotherPrinterSignal() !== null;
  }

  /** Discover USB/Wi‑Fi Brother printer and store session (Forms unlock FAB / agent receipt). */
  async connectAndroidBrotherPrinter(): Promise<void> {
    if (!this.isAndroidNativeBrotherPrint()) {
      return;
    }
    let picked: BrotherAndroidSessionPrinter | null;
    try {
      picked = await this.discoverAndPickBrotherAndroidPrinter();
    } catch (err) {
      const msg = this.getErrorMessage(err);
      await this.toastService.show({
        message: msg,
        duration: 5000,
        color: 'danger',
      });
      return;
    }
    if (!picked) {
      await this.toastService.show({
        message: 'Printer setup cancelled.',
        duration: 2500,
        color: 'medium',
      });
      return;
    }
    this.androidBrotherPrinterSignal.set(picked);
    await this.toastService.show({
      message: `Printer connected (${this.formatPrinterName(picked)}).`,
      duration: 2500,
      color: 'success',
    });
  }

  disconnectAndroidBrotherPrinter(): void {
    this.androidBrotherPrinterSignal.set(null);
    void this.toastService.show({
      message: 'Printer disconnected.',
      duration: 2000,
      color: 'medium',
    });
  }

  /**
   * Build ticket display data. Pass `printTicket` from the session form at print time
   * (FORM orders); when omitted, defaults apply for captions and QR visibility.
   */
  buildTicketData(order: Order, printTicket?: SessionFormPrintTicket | null): OrderTicketData {
    const pt = printTicket ?? {};
    const orderNumber = order.sessionSequence != null ? String(order.sessionSequence) : order.id;
    const createdAt = formatTicketDate(order.createdAt);
    const customerName = (order.data && order.data['customerIdentifier']) || null;
    const queueName = order.queueName?.trim() || null;
    const meta = order.sourceMetadata ?? {};
    const tenantName = meta['tenantName'];
    const workspaceName = meta['workspaceName'];
    const sessionName = meta['sessionName'];

    const showNumberLabel = pt.printTicketShowNumberLabel !== false;
    let numberLabelText = DEFAULT_PRINT_TICKET_NUMBER_LABEL;
    if (typeof pt.printTicketNumberLabel === 'string' && pt.printTicketNumberLabel.trim()) {
      numberLabelText = String(pt.printTicketNumberLabel).trim();
    }

    const showQrCaption = pt.printTicketShowQrCaption !== false;
    let qrCaptionText = DEFAULT_PRINT_TICKET_QR_CAPTION;
    if (typeof pt.printTicketQrCaption === 'string' && pt.printTicketQrCaption.trim()) {
      qrCaptionText = String(pt.printTicketQrCaption).trim();
    }

    const showQueuePrefix = pt.printTicketShowQueuePrefix === true;
    let queuePrefixChars = DEFAULT_PRINT_TICKET_QUEUE_PREFIX;
    if (typeof pt.printTicketQueuePrefix === 'string') {
      queuePrefixChars = String(pt.printTicketQueuePrefix);
    }

    return {
      orderNumber,
      orderId: order.id,
      tenantName: tenantName != null ? String(tenantName) : null,
      workspaceName: workspaceName != null ? String(workspaceName) : null,
      sessionName: sessionName != null ? String(sessionName) : null,
      customerName: customerName != null ? String(customerName) : null,
      queueName,
      createdAt,
      showNumberLabel,
      numberLabelText,
      showQrCaption,
      qrCaptionText,
      showQueuePrefix,
      queuePrefixChars,
    };
  }

  private buildOrderFollowQrUrl(order: Order): string | null {
    const id = order.orderFollowShortUrlId?.trim();
    if (!id) {
      return null;
    }
    const origin = (this.formsPublicOrigin ?? '').trim().replace(/\/$/, '');
    if (!origin) {
      return null;
    }
    return `${origin}/o/${encodeURIComponent(id)}`;
  }

  private async buildQrForOrder(
    order: Order,
  ): Promise<{ dataUrl: string; linkUrl: string | null }> {
    const linkUrl = this.buildOrderFollowQrUrl(order);
    const payload = linkUrl ?? order.id;
    const dataUrl = await QRCode.toDataURL(payload, { width: 200, margin: 2 });
    return { dataUrl, linkUrl };
  }

  private buildTicketStyles(): string {
    return `
      .order-ticket { font-family: Arial, sans-serif; color: #000; text-align: center; }
      .ticket-tenant-name { font-size: 18px; line-height: 1.2; margin: 0 0 8px; font-weight: 700; overflow-wrap: anywhere; }
      .ticket-context { font-size: 17px; line-height: 1.2; margin: 0 0 12px; font-weight: 700; }
      .ticket-section {
        margin: 12px -14px 0 -14px;
        width: calc(100% + 28px);
        border-top: 1px solid #000;
        padding-top: 10px;
        background: #fff;
        box-sizing: border-box;
      }
      .ticket-section--web {
        margin: 12px -16px 0 -16px;
        width: calc(100% + 32px);
      }
      .ticket-caption {
        font-size: 17px;
        line-height: 1.3;
        margin: 0 14px 10px;
        font-weight: 700;
        overflow-wrap: anywhere;
        color: #222;
      }
      .ticket-order-number { color: #000; font-size: 42px; line-height: 1; margin: 0 0 12px; font-weight: 600; letter-spacing: 0.01em; }
      .ticket-section .ticket-order-number:last-child { margin-bottom: 0; }
      .ticket-order-id { font-size: 12px; line-height: 1.25; margin: 0 0 14px; font-weight: 600; color: #000; overflow-wrap: anywhere; }
      .ticket-details-wrap { margin: 12px 14px 0; text-align: center; }
      .ticket-details-wrap--web { margin: 12px 16px 0; }
      .ticket-detail-row {
        margin: 8px 0 0;
        font-size: 17px;
        line-height: 1.35;
        font-weight: 600;
        color: #000;
        overflow-wrap: anywhere;
        word-break: break-word;
      }
      .ticket-detail-row:first-child { margin-top: 0; }
      .ticket-qr-wrap { margin: 14px -14px 0 -14px; width: calc(100% + 28px); padding: 0 14px; box-sizing: border-box; background: #fff; text-align: center; }
      .ticket-qr-wrap--web { margin: 14px -16px 0 -16px; width: calc(100% + 32px); padding: 0 16px; }
      .ticket-qr-wrap .ticket-caption { margin-left: 0; margin-right: 0; }
      .ticket-qr-caption {
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }
      .ticket-qr { margin-top: 6px; text-align: center; }
      .ticket-qr img { width: 180px; height: 180px; }
      .ticket-qr-hint {
        margin: 12px 0 0;
        color: #000;
        text-align: center;
      }
      .ticket-qr-hint-label {
        display: block;
        font-size: 16px;
        line-height: 1.3;
        font-weight: 700;
        margin-bottom: 6px;
      }
      .ticket-qr-url {
        display: block;
        font-size: 15px;
        line-height: 1.35;
        font-weight: 600;
        overflow-wrap: anywhere;
        word-break: break-word;
      }
      .ticket-barcode-block {
        margin: 12px -14px -14px -14px;
        width: calc(100% + 28px);
        border-top: 1px solid #000;
        padding-top: 10px;
        background: #fff;
        box-sizing: border-box;
      }
      .ticket-barcode-block--web {
        margin: 12px -16px -16px -16px;
        width: calc(100% + 32px);
      }
      .ticket-barcode-caption {
        font-size: 12px;
        line-height: 1.25;
        margin: 0 14px 8px;
        font-weight: 600;
        overflow-wrap: anywhere;
      }
      .ticket-barcode-wrap {
        margin: 0;
        width: 100%;
        line-height: 0;
        background: #fff;
      }
      .ticket-barcode-wrap img {
        display: block;
        width: 100%;
        height: auto;
        image-rendering: pixelated;
        image-rendering: crisp-edges;
      }
      .ticket-order-id-hri {
        font-size: 12px;
        line-height: 1.25;
        margin: 8px 14px 0;
        font-weight: 600;
        color: #000;
        overflow-wrap: anywhere;
      }
    `;
  }

  private async loadFormPrintTicketSettings(order: Order): Promise<SessionFormPrintTicket | null> {
    const fs = this.firestore;
    if (!fs || order.sourceType !== SourceType.FORM || !order.sourceId?.trim()) {
      return null;
    }
    try {
      const ref = doc(
        fs,
        'tenants',
        order.tenantId,
        'workspaceInstances',
        order.workspaceInstanceId,
        'sessions',
        order.sessionId,
        'formControls',
        order.sourceId,
      );
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        return null;
      }
      const raw = snap.data() as { settings?: FormSettings };
      const pt = raw?.settings?.printTicket;
      return pt && typeof pt === 'object' ? pt : null;
    } catch (err) {
      console.warn('OrderTicketPrintService: failed to load form printTicket settings', err);
      return null;
    }
  }

  private shouldIncludeQr(order: Order, printTicket: SessionFormPrintTicket | null): boolean {
    if (order.sourceType !== SourceType.FORM) {
      return true;
    }
    if (printTicket && typeof printTicket.includeQr === 'boolean') {
      return printTicket.includeQr;
    }
    return true;
  }

  private shouldIncludeBarcode(): boolean {
    return true;
  }

  private buildTicketHtml(
    data: OrderTicketData,
    qrDataUrl: string | null,
    qrLinkUrl: string | null,
    barcodeDataUrl: string | null,
    barcodeVariant: 'android' | 'web',
  ): string {
    const tenantRow = data.tenantName
      ? `<div class="ticket-tenant-name">${escapeHtml(data.tenantName)}</div>`
      : '';

    const contextLine = [data.workspaceName, data.sessionName]
      .filter((value): value is string => !!value)
      .map((value) => escapeHtml(value))
      .join(' &middot; ');

    const orderIdFallbackRow = barcodeDataUrl
      ? ''
      : `<div class="ticket-order-id">Order ID: ${escapeHtml(data.orderId)}</div>`;

    const barcodeBlockClass =
      barcodeVariant === 'web'
        ? 'ticket-barcode-block ticket-barcode-block--web'
        : 'ticket-barcode-block';

    const sectionClass =
      barcodeVariant === 'web' ? 'ticket-section ticket-section--web' : 'ticket-section';

    const numberCaption = data.showNumberLabel
      ? `<div class="ticket-caption">${escapeHtml(data.numberLabelText)}</div>`
      : '';

    const heroBlock = `<div class="${sectionClass}">${numberCaption}<div class="ticket-order-number">${escapeHtml(data.orderNumber)}</div></div>`;

    const queueLineText =
      data.queueName != null && data.queueName !== ''
        ? data.showQueuePrefix && data.queuePrefixChars
          ? `${data.queuePrefixChars}${data.queueName}`
          : data.queueName
        : '';

    const detailsWrapClass =
      barcodeVariant === 'web'
        ? 'ticket-details-wrap ticket-details-wrap--web'
        : 'ticket-details-wrap';

    const customerDisplay = data.customerName ? escapeHtml(data.customerName) : '—';
    const queueDisplay = queueLineText !== '' ? escapeHtml(queueLineText) : '—';

    const detailsRows = `<div class="${detailsWrapClass}">
      <div class="ticket-detail-row">${customerDisplay}</div>
      <div class="ticket-detail-row">${queueDisplay}</div>
      <div class="ticket-detail-row">${escapeHtml(data.createdAt)}</div>
    </div>`;

    const qrCaption =
      qrDataUrl && data.showQrCaption
        ? `<div class="ticket-caption ticket-qr-caption">${escapeHtml(data.qrCaptionText)}</div>`
        : '';

    const qrWrapClass =
      barcodeVariant === 'web' ? 'ticket-qr-wrap ticket-qr-wrap--web' : 'ticket-qr-wrap';

    const qrHint =
      qrDataUrl && qrLinkUrl
        ? `<div class="ticket-qr-hint"><span class="ticket-qr-hint-label">OR VISIT</span><span class="ticket-qr-url">${escapeHtml(qrLinkUrl)}</span></div>`
        : '';

    const qrBlock = qrDataUrl
      ? `<div class="${qrWrapClass}">${qrCaption}<div class="ticket-qr"><img src="${qrDataUrl}" alt="QR" /></div>${qrHint}</div>`
      : '';

    const barcodeBlock = barcodeDataUrl
      ? `<div class="${barcodeBlockClass}">
        <div class="ticket-barcode-caption">Staff use only</div>
        <div class="ticket-barcode-wrap"><img src="${barcodeDataUrl}" alt="" /></div>
        <div class="ticket-order-id-hri">${escapeHtml(data.orderId)}</div>
      </div>`
      : '';

    return `
      <div class="order-ticket">
        ${tenantRow}
        ${contextLine ? `<div class="ticket-context">${contextLine}</div>` : ''}
        ${heroBlock}
        ${detailsRows}
        ${orderIdFallbackRow}
        ${qrBlock}
        ${barcodeBlock}
      </div>
    `;
  }

  async printOrderTicket(order: Order): Promise<void> {
    const isAndroid = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';

    if (isAndroid) {
      await this.printOrderTicketAndroid(order);
    } else {
      await this.printOrderTicketWeb(order);
    }
  }

  private async printOrderTicketWeb(order: Order): Promise<void> {
    const printTicket = await this.loadFormPrintTicketSettings(order);
    const data = this.buildTicketData(order, printTicket);
    const qr = this.shouldIncludeQr(order, printTicket) ? await this.buildQrForOrder(order) : null;
    const qrDataUrl = qr?.dataUrl ?? null;
    const qrLinkUrl = qr?.linkUrl ?? null;
    const barcodeDataUrl = this.shouldIncludeBarcode()
      ? generateOrderTicketBarcodeDataUrl(order.id, WEB_TICKET_BARCODE_MAX_WIDTH_PX)
      : '';
    const html = this.buildTicketHtml(data, qrDataUrl, qrLinkUrl, barcodeDataUrl || null, 'web');

    const wrap = document.createElement('div');
    wrap.className = 'order-ticket-print-container';
    wrap.innerHTML = `
      <style>
        .order-ticket-print-container { position: fixed; left: -9999px; top: 0; width: 400px; padding: 16px; background: #fff; }
        ${this.buildTicketStyles()}
        @media print {
          @page { size: A4; }
          body * { visibility: hidden; }
          .order-ticket-print-container, .order-ticket-print-container * { visibility: visible; }
          .order-ticket-print-container { position: absolute; left: 0; top: 0; width: 100%; }
        }
      </style>
      ${html}
    `;
    document.body.appendChild(wrap);
    await this.waitForPrintReady(2000);
    window.print();
    document.body.removeChild(wrap);
  }

  private waitForPrintReady(timeoutMs: number): Promise<void> {
    const layoutAndDelay = new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => resolve(), 150);
        });
      });
    });
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, timeoutMs));
    return Promise.race([layoutAndDelay, timeout]);
  }

  /**
   * USB/Wi‑Fi discovery and picker only (used by Connect). Longer timeouts than legacy inline search.
   */
  private async discoverAndPickBrotherAndroidPrinter(): Promise<BrotherAndroidSessionPrinter | null> {
    const { BrotherPrint } = await import('@rdlabo/capacitor-brotherprint');
    const { BrotherPrintEventsEnum, BRLMPrinterPort } =
      await import('@rdlabo/capacitor-brotherprint');

    const printers: Array<{
      port: (typeof BRLMPrinterPort)[keyof typeof BRLMPrinterPort];
      channelInfo: string;
      nodeName: string;
    }> = [];

    let statusToast: Awaited<ReturnType<CrowdctrlToastService['show']>> | null = null;
    const dismissStatus = async () => {
      const toast = statusToast;
      statusToast = null;
      if (toast) {
        await toast.dismiss();
      }
    };
    const showStatus = async (message: string) => {
      await dismissStatus();
      statusToast = await this.toastService.show({
        message,
        sticky: true,
        color: 'medium',
        showDismissButton: false,
      });
    };

    const addListener = BrotherPrint.addListener.bind(BrotherPrint);
    const handleAvailable = (p: {
      port: (typeof BRLMPrinterPort)[keyof typeof BRLMPrinterPort];
      channelInfo: string;
      nodeName: string;
    }) => {
      const existing = printers.find(
        (printer) => printer.port === p.port && printer.channelInfo === p.channelInfo,
      );
      if (existing) {
        existing.nodeName = p.nodeName ?? p.channelInfo;
        return;
      }

      printers.push({
        port: p.port,
        channelInfo: p.channelInfo,
        nodeName: p.nodeName ?? p.channelInfo,
      });
    };

    const handler = await addListener(BrotherPrintEventsEnum.onPrinterAvailable, handleAvailable);

    try {
      try {
        await showStatus('Searching USB Brother printer...');
        await this.withTimeout(
          BrotherPrint.search({
            port: BRLMPrinterPort.usb,
            searchDuration: Math.ceil(ANDROID_CONNECT_USB_SEARCH_MS / 1000),
          }),
          ANDROID_CONNECT_USB_SEARCH_MS,
          'USB printer search timed out.',
        );
      } catch (error) {
        console.warn('USB Brother printer search failed or timed out', error);
      }

      let chosen = await this.waitForPrinter(
        () => printers.find((printer) => printer.port === BRLMPrinterPort.usb),
        ANDROID_CONNECT_USB_WAIT_MS,
      );

      if (!chosen) {
        await dismissStatus();
        const fallbackChoice = await this.promptForUsbFallback();
        if (fallbackChoice === 'cancel') {
          return null;
        }

        if (fallbackChoice === 'retry-usb') {
          try {
            await showStatus('Still checking USB Brother printer...');
            await this.withTimeout(
              BrotherPrint.search({
                port: BRLMPrinterPort.usb,
                searchDuration: Math.ceil(ANDROID_CONNECT_USB_RETRY_SEARCH_MS / 1000),
              }),
              ANDROID_CONNECT_USB_RETRY_SEARCH_MS,
              'Second USB printer search timed out.',
            );
          } catch (error) {
            console.warn('Second USB Brother printer search failed or timed out', error);
          }

          chosen = await this.waitForPrinter(
            () => printers.find((printer) => printer.port === BRLMPrinterPort.usb),
            ANDROID_CONNECT_USB_RETRY_WAIT_MS,
          );
        }
      }

      if (!chosen) {
        try {
          await showStatus('No USB printer found yet. Searching Wi-Fi...');
          await BrotherPrint.search({
            port: BRLMPrinterPort.wifi,
            searchDuration: WIFI_SEARCH_DURATION_SEC,
          });
          chosen = await this.waitForPrinter(() => printers[0], WIFI_PRINTER_WAIT_MS);
        } catch (error) {
          console.warn('Wi-Fi Brother printer search failed', error);
        } finally {
          await BrotherPrint.cancelSearchWiFiPrinter().catch((error: unknown) => {
            console.warn('Failed to cancel Wi-Fi printer search', error);
          });
        }
      }

      chosen =
        chosen ?? printers.find((printer) => printer.port === BRLMPrinterPort.usb) ?? printers[0];

      if (!chosen) {
        throw new Error(
          'No Brother printer found. Check USB, Wi-Fi, printer power, and app USB permission.',
        );
      }

      if (printers.length > 1) {
        await dismissStatus();
        const sheet = await this.actionSheetController.create({
          header: 'Select printer',
          buttons: [
            ...printers.map((p, i) => ({
              text: p.nodeName || p.channelInfo || `Printer ${i + 1}`,
              handler: () => {
                chosen = printers[i];
              },
            })),
            { text: 'Cancel', role: 'cancel' },
          ],
        });
        await sheet.present();
        const { role } = await sheet.onDidDismiss();
        if (role === 'cancel') {
          return null;
        }
      }

      return {
        port: String(chosen.port),
        channelInfo: chosen.channelInfo,
        nodeName: chosen.nodeName,
      };
    } finally {
      await handler.remove();
      await dismissStatus();
    }
  }

  private async printOrderTicketAndroid(order: Order): Promise<void> {
    const session = this.androidBrotherPrinterSignal();
    if (!session) {
      throw new Error(
        'No Brother printer connected. Unlock the form and tap Connect printer first.',
      );
    }

    const { BrotherPrint } = await import('@rdlabo/capacitor-brotherprint');
    const {
      BrotherPrintEventsEnum,
      BRLMPrinterModelName,
      BRLMPrinterLabelName,
      BRLMPrinterHalftone,
      BRLMPrinterPrintQuality,
    } = await import('@rdlabo/capacitor-brotherprint');

    const chosen = {
      port: session.port as Parameters<typeof BrotherPrint.printImage>[0]['port'],
      channelInfo: session.channelInfo,
      nodeName: session.nodeName,
    };

    let statusToast: Awaited<ReturnType<CrowdctrlToastService['show']>> | null = null;
    const dismissStatus = async () => {
      const toast = statusToast;
      statusToast = null;
      if (toast) {
        await toast.dismiss();
      }
    };
    const showStatus = async (message: string) => {
      await dismissStatus();
      statusToast = await this.toastService.show({
        message,
        sticky: true,
        color: 'medium',
        showDismissButton: false,
      });
    };

    const addListener = BrotherPrint.addListener.bind(BrotherPrint);

    try {
      const printTicket = await this.loadFormPrintTicketSettings(order);
      const data = this.buildTicketData(order, printTicket);
      const qr = this.shouldIncludeQr(order, printTicket)
        ? await this.buildQrForOrder(order)
        : null;
      const qrDataUrl = qr?.dataUrl ?? null;
      const qrLinkUrl = qr?.linkUrl ?? null;
      const barcodeDataUrl = this.shouldIncludeBarcode()
        ? generateOrderTicketBarcodeDataUrl(order.id, TICKET_RENDER_WIDTH_PX)
        : '';
      const html = this.buildTicketHtml(
        data,
        qrDataUrl,
        qrLinkUrl,
        barcodeDataUrl || null,
        'android',
      );

      await showStatus('Rendering ticket image...');
      const base64 = await this.withTimeout(
        this.renderTicketImage(html),
        RENDER_TIMEOUT_MS,
        'Timed out generating the ticket image.',
      );

      if (!base64) {
        throw new Error('Failed to generate ticket image.');
      }

      let printerError: string | undefined;
      const printHandler = await addListener(
        BrotherPrintEventsEnum.onPrintError,
        (info: { message?: string; code?: number }) => {
          printerError = this.describeBrotherError('Brother printer error', info);
          console.warn('Brother printer error', info);
        },
      );
      const commHandler = await addListener(
        BrotherPrintEventsEnum.onPrintFailedCommunication,
        (info: { message?: string; code?: number }) => {
          printerError = this.describeBrotherError('Brother printer communication failed', info);
          console.warn('Brother printer communication failed', info);
        },
      );

      try {
        await showStatus(`Sending ticket to ${this.formatPrinterName(chosen)}...`);
        const printOptions: Parameters<typeof BrotherPrint.printImage>[0] = {
          encodedImage: base64,
          modelName: BRLMPrinterModelName.QL_820NWB,
          labelName: BRLMPrinterLabelName.RollW54,
          port: chosen.port,
          channelInfo: chosen.channelInfo,
          numberOfCopies: 1,
          autoCut: true,
          printQuality: BRLMPrinterPrintQuality.Best,
          halftone: BRLMPrinterHalftone.ErrorDiffusion,
        };
        await this.withTimeout(
          BrotherPrint.printImage(printOptions),
          PRINT_TIMEOUT_MS,
          'Printer did not respond within 25 seconds. It may still print; check the printer, USB hub, and roll status.',
        );
      } catch (error) {
        const message = printerError ?? this.getErrorMessage(error);
        throw new Error(
          `${message} Check USB, printer power, and that the loaded roll matches 54mm continuous (non-adhesive).`,
        );
      } finally {
        await printHandler.remove();
        await commHandler.remove();
      }

      await dismissStatus();
      await this.toastService.show({
        message: 'Ticket sent to printer.',
        duration: 2000,
        color: 'success',
      });
    } finally {
      await dismissStatus();
    }
  }

  private async renderTicketImage(html: string): Promise<string> {
    const wrap = document.createElement('div');
    wrap.className = 'order-ticket-android-render';
    wrap.style.cssText = `position:fixed;left:-9999px;top:0;width:${TICKET_RENDER_WIDTH_PX}px;padding:14px;background:#fff;color:#000;font-family:Arial,sans-serif;text-align:center;`;
    wrap.innerHTML = `<style>${this.buildTicketStyles()}</style>${html}`;
    document.body.appendChild(wrap);

    try {
      const canvas = await html2canvas(wrap, {
        scale: TICKET_RENDER_SCALE,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });
      return canvas.toDataURL('image/png').split(',')[1] ?? '';
    } finally {
      document.body.removeChild(wrap);
    }
  }

  private async promptForUsbFallback(): Promise<'retry-usb' | 'wifi' | 'cancel'> {
    let choice: 'retry-usb' | 'wifi' | 'cancel' = 'retry-usb';
    const alert = await this.alertController.create({
      header: 'USB printer not found yet',
      message:
        'The printer may still be starting, waiting for USB permission, or slow through the hub.',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          handler: () => {
            choice = 'cancel';
          },
        },
        {
          text: 'Search Wi-Fi',
          handler: () => {
            choice = 'wifi';
          },
        },
        {
          text: 'Keep waiting for USB',
          handler: () => {
            choice = 'retry-usb';
          },
        },
      ],
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    return role === 'cancel' ? 'cancel' : choice;
  }

  private async withTimeout<T>(
    promise: Promise<T>,
    timeoutMs: number,
    timeoutMessage: string,
  ): Promise<T> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
    });

    try {
      return await Promise.race([promise, timeout]);
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }

  private waitForPrinter<T>(
    getPrinter: () => T | undefined,
    timeoutMs: number,
  ): Promise<T | undefined> {
    const existing = getPrinter();
    if (existing) {
      return Promise.resolve(existing);
    }

    return new Promise((resolve) => {
      const startedAt = Date.now();
      const interval = setInterval(() => {
        const printer = getPrinter();
        if (printer || Date.now() - startedAt >= timeoutMs) {
          clearInterval(interval);
          resolve(printer);
        }
      }, 100);
    });
  }

  private formatPrinterName(printer: { nodeName: string; channelInfo: string }): string {
    return printer.nodeName || printer.channelInfo || 'Brother printer';
  }

  private describeBrotherError(prefix: string, info: { message?: string; code?: number }): string {
    const code = info.code != null ? ` (${info.code})` : '';
    return info.message ? `${prefix}${code}: ${info.message}` : `${prefix}${code}.`;
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;
    if (error && typeof error === 'object' && 'message' in error) {
      const message = (error as { message?: unknown }).message;
      if (typeof message === 'string') return message;
    }
    return 'Printing failed.';
  }
}

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}
